/**
 * Noendy Header Sidebar — MedicBlu pattern (Vanilla JS, Light DOM port)
 *
 * Custom element <noendy-header-sidebar>:
 *   - Open/close (trigger button, overlay, ESC, close button)
 *   - Multi-level navigation (main panel ↔ sub-panel slide)
 *   - WAAPI animation: overlay opacity, content translateX(-100%) → 0
 *   - Body scroll lock (html.noendy-drawer-open class)
 *   - Document outside-click → close
 *   - Focus restore (trigger element'e)
 *
 * Data attributes:
 *   - [aria-controls="noendy-header-sidebar"] → trigger
 *   - [data-noendy-sidebar-close] → overlay + close button
 *   - [data-noendy-sidebar-panel-open="panel-X"] → sub-panel açar
 *   - [data-noendy-sidebar-panel-back] → main panel'e döner
 *   - [data-noendy-sidebar-panel="X"] → panel container
 */
(function () {
  if (customElements.get('noendy-header-sidebar')) return;

  var EASING = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
  var DURATION = 300;
  var SUB_PANEL_DURATION = 250;
  var ITEM_STAGGER_DELAY = 60; // ms — MedicBlu pattern 0.1s arası, biraz hızlı

  class NoendyHeaderSidebar extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._activePanel = 'main';
      this._lastTrigger = null;
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.overlay = this.querySelector('.noendy-header-sidebar__overlay');
      this.content = this.querySelector('.noendy-header-sidebar__content');
      this.panels = this.querySelector('.noendy-header-sidebar__panels');

      var self = this;
      var modalId = this.id;

      // Trigger binding (hamburger button → toggle)
      this._triggers = Array.prototype.slice.call(
        document.querySelectorAll('[aria-controls="' + modalId + '"]')
      );
      this._triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function (event) {
          event.preventDefault();
          if (self._open) self.close();
          else self.open(trigger);
        });
      });

      // Close: overlay click + X close button (both [data-noendy-sidebar-close])
      this.querySelectorAll('[data-noendy-sidebar-close]').forEach(function (el) {
        el.addEventListener('click', function (event) {
          event.preventDefault();
          self.close();
        });
      });

      // Sub-panel open (kategori click)
      this.querySelectorAll('[data-noendy-sidebar-panel-open]').forEach(function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          var panelId = btn.getAttribute('data-noendy-sidebar-panel-open');
          self.switchPanel(panelId);
        });
      });

      // Back button (sub-panel → main)
      this.querySelectorAll('[data-noendy-sidebar-panel-back]').forEach(function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          self.switchPanel('main');
        });
      });

      // ESC keyboard
      document.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('click', this._onDocumentClick, true);
    }

    _onKeyDown(event) {
      if (event.key === 'Escape' && this._open) {
        event.preventDefault();
        this.close();
      }
    }

    /**
     * Document outside-click — overlay header'ı kapatmaz (drawer sol kenara
     * yapışık, geri kalan tüm alan overlay'in alt+sağ kısmı). Trigger'a
     * yeniden tıklama toggle eder, drawer içine tıklama drawer'da kalır.
     */
    _onDocumentClick(event) {
      if (!this._open) return;
      if (this.contains(event.target)) return;
      if (event.target.closest('[aria-controls="' + this.id + '"]')) return;
      this.close();
    }

    open(triggerEl) {
      if (this._open) return;
      this._open = true;
      this._lastTrigger = triggerEl || null;

      // Drawer açılırken: gizli sticky header'ı zorla göster + bottom'u tazele.
      // Aşağı scroll edilmiş sayfada hamburger'a basıldığında header gizli olabilir;
      // bu durumda --noendy-header-bottom yanlış değerde kalır ve drawer üstte boşluk
      // bırakır. Header'ı reveal edip değeri yeniden hesaplıyoruz.
      var sectionHeader = document.querySelector('.section-header');
      if (sectionHeader && sectionHeader.classList.contains('shopify-section-header-hidden')) {
        sectionHeader.classList.remove('shopify-section-header-hidden');
      }
      var headerEl = (sectionHeader && sectionHeader.querySelector('.header')) || sectionHeader;
      if (headerEl) {
        var headerBottom = Math.round(headerEl.getBoundingClientRect().bottom);
        if (headerBottom > 0) {
          document.documentElement.style.setProperty('--noendy-header-bottom', headerBottom + 'px');
        }
      }

      // Reset to main panel
      this._activePanel = 'main';
      this._showPanel('main', { instant: true });
      // Items stagger reveal — drawer slide-in tamamlanırken items sırayla görünür
      var mainPanel = this.querySelector('[data-noendy-sidebar-panel="main"]');
      if (mainPanel) {
        var self = this;
        setTimeout(function () {
          self._staggerItems(mainPanel, false);
        }, DURATION * 0.4);
      }

      this.removeAttribute('hidden');
      void this.offsetWidth;
      this.setAttribute('open', '');
      document.documentElement.classList.add('noendy-drawer-open');
      this._triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });

      // WAAPI: overlay fade-in + content slide-in (translateX(-100%) → 0)
      if (this.overlay && this.overlay.animate) {
        this.overlay.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: DURATION, easing: EASING, fill: 'forwards' }
        );
      }
      if (this.content && this.content.animate) {
        this.content.animate(
          [
            { transform: 'translateX(-100%)' },
            { transform: 'translateX(0)' }
          ],
          { duration: DURATION, easing: EASING, fill: 'forwards' }
        );
      }

      // Document outside-click — setTimeout(0) ile trigger click bubble'ından sonra
      var self = this;
      setTimeout(function () {
        document.addEventListener('click', self._onDocumentClick, true);
      }, 0);
    }

    close() {
      if (!this._open) return;
      this._open = false;

      document.removeEventListener('click', this._onDocumentClick, true);

      var self = this;
      this._triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      var done = function () {
        self.removeAttribute('open');
        self.setAttribute('hidden', '');
        document.documentElement.classList.remove('noendy-drawer-open');
        if (self._lastTrigger && typeof self._lastTrigger.focus === 'function') {
          try { self._lastTrigger.focus(); } catch (e) {}
        }
      };

      // WAAPI exit: overlay fade-out + content slide-out (0 → -100%)
      var anims = [];
      if (this.overlay && this.overlay.animate) {
        var overlayAnim = this.overlay.animate(
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: DURATION, easing: EASING, fill: 'forwards' }
        );
        if (overlayAnim.finished) anims.push(overlayAnim.finished);
      }
      if (this.content && this.content.animate) {
        var contentAnim = this.content.animate(
          [
            { transform: 'translateX(0)' },
            { transform: 'translateX(-100%)' }
          ],
          { duration: DURATION, easing: EASING, fill: 'forwards' }
        );
        if (contentAnim.finished) anims.push(contentAnim.finished);
      }

      if (anims.length === 0) { done(); return; }
      Promise.all(anims).then(done).catch(done);
    }

    /**
     * Panel değiştir — main ↔ sub-panel slide animation.
     * "main" → ana panel görünür, sub-panel'ler hidden.
     * "panel-X" → sub-panel görünür, main hidden.
     */
    switchPanel(panelId) {
      if (this._activePanel === panelId) return;
      this._showPanel(panelId);
      this._activePanel = panelId;
    }

    _showPanel(panelId, options) {
      options = options || {};
      var panels = this.querySelectorAll('[data-noendy-sidebar-panel]');
      var self = this;

      panels.forEach(function (panel) {
        var name = panel.getAttribute('data-noendy-sidebar-panel');
        if (name === panelId) {
          panel.hidden = false;
          if (!options.instant && panel.animate) {
            panel.animate(
              [
                { opacity: 0, transform: 'translateX(8px)' },
                { opacity: 1, transform: 'translateX(0)' }
              ],
              { duration: SUB_PANEL_DURATION, easing: EASING, fill: 'forwards' }
            );
            // Items stagger reveal — MedicBlu pattern (her item sırayla fade-in)
            self._staggerItems(panel, options.instant);
          }
        } else {
          panel.hidden = true;
        }
      });
    }

    /**
     * Items stagger reveal — MedicBlu pattern: linklist items 60ms delay ile
     * sırayla translateY(8px) → 0 + opacity 0 → 1.
     * Back button da varsa o da animation alır (ilk index).
     */
    _staggerItems(panel, instant) {
      if (instant) return;
      var items = panel.querySelectorAll(
        '.noendy-header-sidebar__back-button, .noendy-header-sidebar__linklist > li'
      );
      if (!items.length) return;

      Array.prototype.forEach.call(items, function (item, index) {
        if (!item.animate) return;
        item.animate(
          [
            { opacity: 0, transform: 'translateY(8px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          {
            duration: 250,
            delay: 50 + index * ITEM_STAGGER_DELAY,
            easing: EASING,
            fill: 'both'
          }
        );
      });
    }
  }

  customElements.define('noendy-header-sidebar', NoendyHeaderSidebar);
})();
